import React from 'react';
import { Backdrop, Box, Typography } from "@mui/material";
import Logo from "../Logo";

const ringKeyframe = "LoaderRing";
const pulseKeyframe = "LoaderPulse";

const LoaderMark = ({ size = 42, inline = false }) => (
    <Box
        sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            isolation: "isolate",
            [`@keyframes ${ringKeyframe}`]: {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
            },
            [`@keyframes ${pulseKeyframe}`]: {
                "0%, 100%": { transform: "scale(0.92)", opacity: 0.52 },
                "50%": { transform: "scale(1.08)", opacity: 0.12 },
            },
            "&:before": {
                content: '""',
                position: "absolute",
                inset: inline ? 1 : 0,
                borderRadius: "inherit",
                background: "conic-gradient(from 160deg, #05690D, #1976D2, #78D8A0, #05690D)",
                animation: `${ringKeyframe} 1.15s linear infinite`,
            },
            "&:after": {
                content: '""',
                position: "absolute",
                inset: inline ? -3 : -8,
                borderRadius: "inherit",
                background: "rgba(25,118,210,0.18)",
                animation: `${pulseKeyframe} 1.5s ease-in-out infinite`,
                zIndex: -1,
            },
        }}
    >
        <Box
            sx={{
                width: Math.max(size - 10, 18),
                height: Math.max(size - 10, 18),
                borderRadius: "50%",
                bgcolor: "background.paper",
                display: "grid",
                placeItems: "center",
                boxShadow: inline ? "none" : "0 8px 24px rgba(60,64,67,0.12)",
            }}
        >
            {size >= 24 ? <Logo size={Math.max(Math.round(size * 0.44), 14)} /> : null}
        </Box>
    </Box>
);

const Loader = ({ size: Size = 20, variant = 'inline', open = true, label }) => {
    const message = label || "Working on it...";

    if (variant === 'fullscreen') {
        return (
            <Backdrop
                sx={(theme) => ({
                    color: theme.palette.text.primary,
                    zIndex: theme.zIndex.modal + 1,
                    background:
                        "radial-gradient(circle at 50% 45%, rgba(255,255,255,0.96), rgba(248,250,253,0.88) 42%, rgba(232,244,236,0.72) 100%)",
                    backdropFilter: "blur(8px)",
                    opacity: 1,
                })}
                open={open}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 1.5,
                        px: 2,
                        textAlign: "center",
                    }}
                >
                    <LoaderMark size={48} />
                    <Typography variant="body2" sx={{ fontWeight: 800, color: "#30343b" }}>
                        {message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        This should only take a moment.
                    </Typography>
                </Box>
            </Backdrop>
        );
    }

    if (variant === 'section') {
        return (
            <Box
                sx={{
                    width: '100%',
                    minHeight: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <LoaderMark size={42} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {message}
                </Typography>
            </Box>
        );
    }

    return(
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            padding: '0px',
        }}>
            <LoaderMark size={Size} inline />
        </span>
    );
};

export default Loader;
